import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: false } });
      setItems(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    setOpen(false);
    if (n.link) {
      // strip host if present
      const path = n.link.startsWith("http") ? new URL(n.link).pathname + (new URL(n.link).search || "") : n.link;
      navigate(path);
    }
    load();
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch {}
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[#A3A3A3] hover:text-white relative p-2 hover:bg-[#141414] transition-colors"
        data-testid="notifications-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#FF5A1F] text-black text-[10px] font-bold px-1.5 py-0 min-w-[16px] h-4 inline-flex items-center justify-center" data-testid="notifications-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#0A0A0A] border border-[#262626] z-40 max-h-[70vh] overflow-auto" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626] sticky top-0 bg-[#0A0A0A]">
            <p className="ft-overline">NOTIFICATIONS</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#FF5A1F] hover:underline inline-flex items-center gap-1" data-testid="notifications-mark-all">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="p-6 text-[#737373] text-sm text-center">No notifications yet.</p>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => openItem(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[#1A1A1A] hover:bg-[#141414] transition-colors ${!n.read ? "bg-[#1A1A1A]" : ""}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 bg-[#FF5A1F] rounded-full shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{n.title}</p>
                        <p className="text-[#A3A3A3] text-xs mt-0.5">{n.body}</p>
                        <p className="text-[#737373] text-[10px] font-mono mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
